import { Controller } from "@hotwired/stimulus"

/**
 * UT Equipment Set Selector Controller
 * Handles selecting and applying UT equipment sets to the report form
 * When a set is selected, it replaces all UT equipment with the set's equipment
 */
export default class extends Controller {
  static targets = ["select", "applyButton"]

  connect() {
    this.loadSets()
  }

  /**
   * Load available equipment sets from API
   */
  async loadSets() {
    try {
      const response = await fetch('/ut_equipment_sets.json')
      if (!response.ok) throw new Error('Failed to fetch equipment sets')

      this.sets = await response.json()
      this.populateDropdown()
    } catch (error) {
      console.error('[ut-equipment-set-selector] Error loading sets:', error)
    }
  }

  /**
   * Populate the dropdown with available sets
   */
  populateDropdown() {
    const select = this.selectTarget

    // Clear existing options except the placeholder
    select.innerHTML = '<option value="">Choose a saved equipment set...</option>'

    // Group sets by ownership
    const mySets = this.sets.filter(s => s.is_own)
    const otherSets = this.sets.filter(s => !s.is_own)

    // Add own sets first
    if (mySets.length > 0) {
      const myGroup = document.createElement('optgroup')
      myGroup.label = 'My Sets'
      mySets.forEach(set => {
        const option = document.createElement('option')
        option.value = set.id
        option.textContent = `${set.name} (${set.equipment.length} items)`
        myGroup.appendChild(option)
      })
      select.appendChild(myGroup)
    }

    // Add other users' sets
    if (otherSets.length > 0) {
      const othersGroup = document.createElement('optgroup')
      othersGroup.label = "Other Users' Sets"
      otherSets.forEach(set => {
        const option = document.createElement('option')
        option.value = set.id
        option.textContent = `${set.name} by ${set.user_name} (${set.equipment.length} items)`
        othersGroup.appendChild(option)
      })
      select.appendChild(othersGroup)
    }

    // Reinitialize Select2 if it's being used
    if (window.jQuery && window.jQuery(select).data('select2')) {
      window.jQuery(select).trigger('change')
    }
  }

  /**
   * Apply the selected equipment set to the report form
   */
  async applySet(event) {
    // Get value from the select target, not the event target (works for both button click and select change)
    const setId = this.selectTarget.value

    if (!setId) {
      alert('Please select an equipment set first')
      return
    }

    const set = this.sets.find(s => s.id === parseInt(setId))
    if (!set) {
      console.error('[ut-equipment-set-selector] Set not found:', setId)
      return
    }

    // Confirm with user
    if (!confirm(`This will replace all current UT equipment with "${set.name}".\n\nContinue?`)) {
      this.selectTarget.value = ''
      if (window.jQuery && window.jQuery(this.selectTarget).data('select2')) {
        window.jQuery(this.selectTarget).val('').trigger('change')
      }
      return
    }

    try {
      // Fetch detailed set data with equipment by type
      const response = await fetch(`/ut_equipment_sets/${setId}.json`)
      if (!response.ok) throw new Error('Failed to fetch set details')

      const setData = await response.json()

      // Find all ut-equipment controllers on the page
      const utEquipmentControllers = this.findUtEquipmentControllers()

      if (Object.keys(utEquipmentControllers).length === 0) {
        console.error('[ut-equipment-set-selector] No ut-equipment controllers found')
        alert('Error: Could not find equipment sections on page. Please refresh and try again.')
        return
      }

      // Clear all and apply new equipment by type
      Object.entries(utEquipmentControllers).forEach(([typeId, controller]) => {
        // Clear existing
        controller.clearAll()

        // Get equipment for this type
        let equipmentList = []
        switch (parseInt(typeId)) {
          case 1: equipmentList = setData.ut_instruments || []; break
          case 2: equipmentList = setData.ut_probes || []; break
          case 3: equipmentList = setData.ut_calibration_blocks || []; break
          case 4: equipmentList = setData.ut_reference_blocks || []; break
        }

        // Add each equipment item
        equipmentList.forEach((eq) => {
          controller.addEquipmentDirect(eq)
        })

        // Render and update hidden fields
        controller.finishUpdate()
      })

      // Trigger autosave on one of the controllers
      const firstController = Object.values(utEquipmentControllers)[0]
      if (firstController) {
        await firstController.triggerAutosave()
      }

      // Reset the dropdown
      this.selectTarget.value = ''
      if (window.jQuery && window.jQuery(this.selectTarget).data('select2')) {
        window.jQuery(this.selectTarget).val('').trigger('change')
      }

    } catch (error) {
      console.error('[ut-equipment-set-selector] Error applying set:', error)
      alert('Failed to apply equipment set. Please try again.')
    }
  }

  /**
   * Find all ut-equipment Stimulus controllers on the page
   * Returns an object keyed by equipment type ID
   */
  findUtEquipmentControllers() {
    const controllers = {}

    // Find all elements with the ut-equipment controller
    const elements = document.querySelectorAll('[data-controller*="ut-equipment"]')

    elements.forEach((el) => {
      // Skip the set selector itself
      if (el.dataset.controller.includes('ut-equipment-set-selector')) {
        return
      }

      const typeId = el.dataset.utEquipmentTypeValue

      if (typeId) {
        // Get the Stimulus controller instance
        const controller = this.application.getControllerForElementAndIdentifier(el, 'ut-equipment')

        if (controller) {
          controllers[typeId] = controller
        }
      }
    })

    return controllers
  }
}
